/**
 * The Employee page's clock credentials (piece 34, *The employee file*; T2069):
 * Issue PIN, Register Badge and Revoke in the Access section, written through
 * piece 15's issuing service. 🔒 A PIN is drawn on the server and answered
 * once; the page shows it once, and nothing stores or logs it in clear.
 */

/** The two kinds HR issues here; Biometric stays on the device (decision 0127). */
export const ISSUED_CREDENTIAL_KINDS = ['PIN', 'Badge'] as const;
export type IssuedCredentialKind = (typeof ISSUED_CREDENTIAL_KINDS)[number];

/** 🔒 The refusal of an issue or a revoke to anybody without HR's write key. */
export const CLOCK_CREDENTIAL_REFUSAL = 'Issuing and revoking a clock credential are open to a holder of the hire permission, and this identity holds none.';

/** Issue a PIN or register a badge on one job, relative to the API root. */
export function clockCredentialsPath(employmentId: string): string {
  return `/hr/employees/${encodeURIComponent(employmentId)}/clock-credentials`;
}

/** Revoke one credential of one job, relative to the API root. */
export function clockCredentialRevokePath(employmentId: string, credentialId: string): string {
  return `${clockCredentialsPath(employmentId)}/${encodeURIComponent(credentialId)}/revoke`;
}

/** What the Access section sends: a PIN asks nothing more, a badge the number it carries. */
export interface ClockCredentialDraft {
  credentialKind: IssuedCredentialKind;
  badgeNumber?: string;
}

/** The longest badge number kept; a printed card carries far fewer. */
export const BADGE_NUMBER_MAXIMUM = 64;

/** A badge number is four or more letters or digits, with no space inside. */
export function badgeNumberProblem(text: string): string | null {
  const value = text.trim();
  if (value.length < 4) return 'A badge number is at least four characters.';
  if (value.length > BADGE_NUMBER_MAXIMUM) return `A badge number is at most ${BADGE_NUMBER_MAXIMUM} characters.`;
  return /^[A-Za-z0-9-]+$/.test(value) ? null : 'A badge number is letters, digits and dashes.';
}

/** 🔒 The answer to an issue: the PIN once for a PIN, and nothing secret for a badge. */
export interface IssuedCredential {
  id: string;
  credentialKind: IssuedCredentialKind;
  issuedAt: string;
  pin: string | null;
}

/** One active credential as the Access section lists it, never with its secret. */
export interface ActiveCredentialRow {
  id: string;
  credentialKind: string;
  issuedAt: string;
}
